import {
  capitalize, computed, ref, type Ref, unref,
} from 'vue';
import { fetchApi, type FetchQueryOptions, useFetchQuery } from '@hooks/fetch';
import { useLocalStorage } from '@vueuse/core';
import type { HydraCollection } from '@/entities/Entities';

export interface Pagination {
  page: number;
  perPage: number;
}

export interface DateRangeFilter {
  start?: Date;
  end?: Date;
}

export interface NumberRangeFilter {
  from?: number;
  to?: number;
}

export type FilterPart = string | string[] | DateRangeFilter | NumberRangeFilter | boolean;
export type Filter = Record<string, FilterPart>;

export interface Sort {
  sortKey?: string | string[];
  sortDirection?: 'ASC' | 'DESC' | Array<'ASC' | 'DESC'>;
}

export interface ProviderParams {
  pagination?: Pagination | false;
  filter?: Filter;
  sort?: Sort;
  special?: Record<string, string>;
}

const isDateRangeFilter = (value: FilterPart): value is DateRangeFilter =>
  typeof value === 'object' &&
  (('start' in value && value.start instanceof Date) ||
    ('end' in value && value.end instanceof Date));

const isNumberRangeFilter = (value: FilterPart): value is NumberRangeFilter =>
  typeof value === 'object' &&
  (('from' in value && typeof value.from === 'number') ||
    ('to' in value && typeof value.to === 'number'));

const prepareFilter = (filter: Filter): string[][] =>
  Object.entries(filter).flatMap(([key, value]): string[][] => {
    if (value === null || value === undefined || value === '') return [];
    if (typeof value === 'boolean') return [[key, value ? 'true' : 'false']];
    if (Array.isArray(value)) return value.map((v) => [`${key}[]`, v]);
    if (isDateRangeFilter(value)) {
      const after = value.start ? [[`${key}[after]`, value.start.toISOString()]] : [];
      const before = value.end ? [[`${key}[before]`, value.end.toISOString()]] : [];
      return [...after, ...before];
    }
    if (typeof value === 'object' && 'start' in value && 'end' in value && !value.start && !value.end) {
      return [];
    }
    const isNullish = (v: unknown) => v === undefined || v === null || Number.isNaN(v);

    if (isNumberRangeFilter(value)) {
      if (!isNullish(value.from) && !isNullish(value.to)) {
        return [[`${key}[between]`, `${value.from.toString()}..${value.to.toString()}`]];
      }
      if (!isNullish(value.from)) {
        return [[`${key}[gte]`, value.from.toString()]];
      }
      if (!isNullish(value.to)) {
        return [[`${key}[lte]`, value.to.toString()]];
      }
      return [];
    }
    return [[key, value]];
  });

const prepareSort = (sort: Sort): string[][] => {
  const getSortDirection = (index: number) =>
    (Array.isArray(sort.sortDirection) ? sort.sortDirection.at(index) : sort.sortDirection);

  if (Array.isArray(sort.sortKey)) {
    return sort.sortKey.map((key, i) => [`order[${key}]`, getSortDirection(i)] as [string, string]);
  }

  const sortDirection = getSortDirection(0);

  return sort.sortKey
    ? [[`order[${sort.sortKey}]`, sortDirection?.toLocaleLowerCase() ?? 'asc']]
    : [];
};

const preparePagination = (pagination: Pagination | false): string[][] =>
  (pagination === false
    ? [['pagination', 'false']]
    : [
      ['itemsPerPage', pagination.perPage.toString()],
      ['page', pagination.page.toString()],
    ]);

const prepareSpecial = (special: Record<string, string>): string[][] =>
  Object.entries(special)?.map(([key, value]) => [`_special[${key}]`, value]) ?? [];

export const getFilterQuery = (params: ProviderParams) =>
  ([] as string[][]).concat(
    params.filter ? prepareFilter(params.filter) : [],
    params.sort ? prepareSort(params.sort) : [],
    params.pagination !== undefined && params.pagination !== null
      ? preparePagination(params.pagination)
      : [],
    params.special ? prepareSpecial(params.special) : [],
  );

// eslint-disable-next-line import/prefer-default-export
export function useProvider<T = unknown>(
  url: RequestInfo | Ref<RequestInfo>,
  params: ProviderParams | Ref<ProviderParams>,
  options?: FetchQueryOptions,
) {
  const fetchResult = useFetchQuery<T>(
    url,
    computed(() => ({
      query: getFilterQuery(unref(params)),
    })),
    options,
  );
  return {
    ...fetchResult,
    refetch(refetchParams?: typeof params) {
      if (!refetchParams) return fetchResult.refetch();
      return fetchResult.refetch({ query: getFilterQuery(unref(refetchParams)) });
    },
  };
}

export function useProviderOpt<T = unknown, O = unknown>(
  url: RequestInfo | Ref<RequestInfo>,
  initialParams: ProviderParams | Ref<ProviderParams>,
  toOption?: (value: T) => O,
  options?: FetchQueryOptions,
) {
  const reactiveParams = ref(unref(initialParams));

  const fetchResult = useFetchQuery<HydraCollection<T>>(
    url,
    computed(() => ({
      query: getFilterQuery(unref(reactiveParams)),
    })),
    options,
  );

  const transformedData = computed<O[]>(() => {
    const rawData = fetchResult.data.value;

    if (!rawData || !Array.isArray(rawData['hydra:member'])) {
      console.warn("useProviderOpt - Expected 'hydra:member' array, got:", rawData);
      return [];
    }

    return rawData['hydra:member'].map((item) =>
      (toOption ? toOption(item) : (item as unknown as O)));
  });

  const refetch = (refetchParams?: ProviderParams) => {
    if (refetchParams) {
      reactiveParams.value = refetchParams;
    }
    return fetchResult.refetch();
  };

  return {
    ...fetchResult,
    data: transformedData,
    refetch,
  };
}

interface Option {
  value: string | null;
  label: string;
}

type ToOptionFn<T> = (value: T) => Option;
type SelectFn<C, T> = (coll: C) => T[];

const defaultSelect = <C, V>(c: C): V[] => {
  if (Array.isArray(c)) return c as V[];
  return [];
};

interface CacheOptions<T> {
  getCollection: (endpoint: string, filterQuery?: string[][]) => T[] | null;
  setCollection: (value: T[], endpoint: string, filterQuery?: string[][]) => void;
}

export const buildOptionsProvider =
  <C, V>(
    select: SelectFn<C, V> = defaultSelect,
    cacheOptions: CacheOptions<V>|undefined = undefined,
  ) =>
    (
      endpoint: string,
      params?: ProviderParams | ToOptionFn<V>,
      toOption?: ToOptionFn<V>,
      pagination?: boolean,
    ) =>
      async () => {
        if (typeof params === 'function') {
          toOption = params;
          params = {};
        }

        if (!toOption) {
          toOption = (v) => ({ label: capitalize(`${v}`), value: `${v}` });
        }

        const filterQuery = params ? getFilterQuery(params) : [];
        if (!pagination) {
          filterQuery.push(['pagination', 'false']);
        }

        // Return cache if available
        const cachedCollection = cacheOptions?.getCollection(endpoint, filterQuery) ?? null;
        if (cachedCollection !== null) {
          return cachedCollection.map(toOption);
        }

        const response = await fetchApi<C>(endpoint, {
          query: filterQuery,
        });

        const selected = select(response);

        cacheOptions?.setCollection(selected, endpoint, filterQuery);

        return selected.map(toOption) ?? [];
      };

export const enumOptionsCacheKey = 'enum_options_cache';
export const enumOptionsProvider = buildOptionsProvider<string[], string>((c) => c, {
  getCollection: (endpoint) => {
    const enumOptions = useLocalStorage<Record<string, string[]>>(enumOptionsCacheKey, {});
    return unref(enumOptions)?.[endpoint] ?? null;
  },
  setCollection: (value, endpoint) => {
    const enumOptions = useLocalStorage<Record<string, string[]>>(enumOptionsCacheKey, {});
    enumOptions.value[endpoint] = value;
  },
});
export const collOptionsProvider = <V>(
  endpoint: string,
  params: ProviderParams | ToOptionFn<V>,
  toOption?: ToOptionFn<V>,
  pagination?: boolean,
) =>
    buildOptionsProvider<HydraCollection<V>, V>((c) => c?.['hydra:member'])(
      endpoint,
      params,
      toOption,
      pagination,
    );

export const withExtraOptions =
  (
    provider: () => Promise<Option[]>,
    options: Option[],
    merge: (providerOptions: Option[], options: Option[]) => Option[] = (pOpts, mOpts) => [
      ...mOpts,
      ...pOpts,
    ],
  ) =>
    async () => {
      const providerOptions = await provider();
      return merge(providerOptions, options);
    };
